import { AppData, Lightning, Log, Utils } from '@lightningjs/sdk'
import { Helper } from '../lib/helper'
import { Badge, Base, ScrollWrapper, TextBox } from '@lightningjs/ui-components'
import { ItemNode } from '../lib/components/grid/ItemNode'
import { AppSetup } from '../lib/app-setup'
import { AppApiConfig } from '../lib/app-api-config'
import StarUI from './star-ui'
import FallbackImage from './fallback-image'


export default class PlayerInfoUI extends Lightning.Component {

    isVisible = false;

    currentItem: ItemNode | null = null
    pendingLogoUrl: string | null = null

    static override _template(): Lightning.Component.Template {
        return {
            Container: {
                w: (w) => w / 2 - 30,
                h: (h) => h,
                rect: true,
                color: 0xFF0D0D1D,
                alpha: 0.9,
                zIndex: 100,
                LogoContainer: {
                    BackButton: {
                        x: 60,
                        y: 90,
                        collision: true,
                        texture: {
                            src: Utils.asset('images/ico-back.png'),
                            resizeMode: {
                                h: 40,
                                w: 40,
                                type: 'contain',
                            },
                            type: Lightning.textures.ImageTexture
                        }
                    },
                    Logo: { x: 130, y: 80 },
                },
                InfoTextWrapper: {
                    x: 130,
                    y: 142,
                    zIndex: 2,
                    flex: { direction: 'column', },
                    SeriesTitle: { y: 20, text: { text: '', fontSize: 38, fontFace: Helper.getFontFamily('Regular') } },
                    Title: { y: 15, text: { text: '', fontFace: Helper.getFontFamily('Black'), fontSize: 42, wordWrapWidth: 700, maxLines: 2, maxLinesSuffix: '...', textOverflow: 'ellipsis' } },
                    ContentLogo: { visible: false, flexItem: { marginTop: 20 } },
                    ItemMetadataWrapper: {
                        y: 20,
                        flex: { direction: 'row', wrap: false },
                        rect: true, color: 0x00000000,
                        children: []
                    },
                    Description: { y: 30, text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Regular'), wordWrapWidth: 700, lineHeight: 40, letterSpacing: 0.5 }},
                    Actors: { y: 60, alpha: 0, text: { text: ''} },
                    Directors: { y: 50, alpha: 0, text: { text: ''} },
                    MoodsTag: { y: 50, text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: 0x80ffffff } },
                }
            },
            alpha: 0
        }
    }

    // override _init() {
    //     const { appState } = AppData as AppSetup;
    //     const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!;
    //     const bgcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.bgcolor)
    //     this.tag('Container').patch({
    //         color: bgcolor,
    //         alpha: 0.9
    //     })
    // }

    override _init() {
        const contentLogo = this.tag('Container.InfoTextWrapper.ContentLogo')
        contentLogo.on('txError', this._onContentLogoError.bind(this))
    }

    /* Fallback: if the ContentLogo image errors (e.g. 404), show the text Title instead */
    _onContentLogoError() {
        if (!this.pendingLogoUrl || !this.currentItem) return
        const item = this.currentItem
        Log.error('ContentLogo failed to load, falling back to title:', this.pendingLogoUrl)
        this.pendingLogoUrl = null
        this.tag('Container').patch({
            InfoTextWrapper: {
                Title: { visible: true, text: { text: item.title }, color: 0xffffffff },
                ContentLogo: { visible: false },
            },
        })
    }

    show() {
        this.isVisible = true;
        this.visible = true;
        this.setSmooth('alpha', 1)
    }

    hide() {
        this.isVisible = false;
        this.visible = false;
        this.setSmooth('alpha', 0)
    }

    getPatchObjects(item: ItemNode): any {
        const { appState, platform } = AppData as AppSetup;
        const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!;
        const bgcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.bgcolor)
        const textcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.textcolor, 0.5)
        const likeImg = appApiConfig?.appConfig.likeImg;
        const dislikeImg = appApiConfig?.appConfig.dislikeImg;
        const loveitImg = appApiConfig?.appConfig.loveitImg
        const description = Helper.getLocalizedDescription(item)

        const infoObj: any = {
            SeriesTitle: { text: { text: item.series_name || '' } },
            ItemMetadataWrapper: {
                children: []
            },
            Description: { text: { text: description } },
            MoodsTag: { text: { text: item.mood_tags ? item.mood_tags.split(',').join('  •  ') : '' } }
        }

        this.currentItem = item
        const contentLogoImg = (item.content_logo || '').trim()
        if (appApiConfig?.appConfig?.enableContentLogo && contentLogoImg) {
            this.pendingLogoUrl = contentLogoImg
            infoObj.Title = { visible: false }
            infoObj.ContentLogo = {
                visible: true,
                texture: {
                    src: contentLogoImg,
                    type: Lightning.textures.ImageTexture,
                    resizeMode: { type: 'contain', h: 70 }
                },
            }
        } else {
            this.pendingLogoUrl = null
            infoObj.Title = { visible: true, text: { text: item.title }, color: 0xffffffff }
            infoObj.ContentLogo = { visible: false }
        }

        const airingPeriodAlpha = (item.airing_period == null || item.airing_period === '' ? 0 : 1)
        const runtimeAlpha = (item.runtime == 0 || airingPeriodAlpha == 1 ? 0 : 1)
        const ageRatingAlpha = (item.age_appropriate_rating == '' ? 0 : 1)
        const popularityRatingAlpha = (item.popularity_rating == -1 ? 0 : 1)
        const releaseDate = item.release_date
        const actorsAlpha = (item.actors == null ? 0 : 0.7)
        const directorsAlpha = (item.directors == null ? 0 : 0.7)
        const likeDisLikeAlpha = (item.likedislike == null ? 0 : Number(item.likedislike))

        const releaseDateRuntime = [
            releaseDate !== '' ? releaseDate : null,
            runtimeAlpha == 1 ? Helper.secondsToHms(item.runtime!) : null,
        ].filter((v): v is string => v !== null).join(' • ')

        if (releaseDateRuntime !== '' && airingPeriodAlpha == 0) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 50,
                // y: 8,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: { text: releaseDateRuntime, fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: Helper.getHexaColor("ffffff", 0.5), },
            })
        }
         if (airingPeriodAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 40,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: { text: item.airing_period, fontSize: 24, fontFace: Helper.getFontFamily('Regular'), textColor: Helper.getHexaColor("ffffff", 0.5) },
            })
        }
        if (ageRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                type: Badge,
                title: item.age_appropriate_rating, style: { paddingX: 16, paddingY: 3, strokeWidth: 0, backgroundColor: textcolor, radius: 0, textStyle: { fontSize: 20, textColor: bgcolor, fontFace: Helper.getFontFamily('Regular') } }
            })
        }

        const isMultiLangEnabled = Helper.hasSupportedMultiLangDescription(item);

        if (isMultiLangEnabled) {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset('images/ico-multi-lang.png'),
                    type: Lightning.textures.ImageTexture
                }
            })
        }

        const ccIcon = Helper.getCCIcon(item);
        if (ccIcon !== '') {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset(ccIcon),
                    type: Lightning.textures.ImageTexture
                }
            })
        }

        if (likeDisLikeAlpha !== 0) {
            let icon = '';
            let isApiIcon = false;
            if (likeDisLikeAlpha == 1) {
                icon = 'images/ico-thumbsup.png';
                // isApiIcon = !!likeImg;
            }
            else if (likeDisLikeAlpha == 2) {
                icon = 'images/ico-love.png';
                // isApiIcon = !!loveitImg;
            }
            else if (likeDisLikeAlpha == 3) {
                icon = 'images/ico-thumbsdown.png';
                // isApiIcon = !!dislikeImg;
            }

            if (icon !== '') {
                infoObj.ItemMetadataWrapper.children.push({
                    w: 36, h: 36,
                    flexItem: { marginLeft: 0, marginRight: 35 },
                    alpha: 0.5,
                    type: FallbackImage,
                    imgSrc: icon,
                })
            }
        }

        if (popularityRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                alpha: popularityRatingAlpha,
                type: StarUI,
                rating: item.popularity_rating,
            })
        }

        if (actorsAlpha) {
          infoObj.Actors = {
            alpha: actorsAlpha,
            text: { text: 'Cast: ' + item.actors.join(', '), fontSize: 24, fontFace: 'Light', wordWrapWidth: 700, lineHeight: 40 },
          }
        }
        if (directorsAlpha) {
          infoObj.Directors = {
            alpha: directorsAlpha,
            text: { text: 'Creators: ' + item.directors.join(', '), fontSize: 24, fontFace: 'Light', wordWrapWidth: 700, lineHeight: 40 },
          }
        }
        this._updateLogo()

        const displayRating = Math.round((item.popularity_rating! / 100) * 5 * 10) / 10
        const speak = (text: string) => text ? `. ${text}` : ''
        const periodOrRuntime = airingPeriodAlpha == 1 ? item.airing_period : releaseDateRuntime
        const speechText = item.title +
            speak(periodOrRuntime) +
            speak(ageRatingAlpha == 1 ? `Rated ${item.age_appropriate_rating}` : '') +
            speak(ccIcon !== '' ? 'Closed Captions available' : '') +
            speak(popularityRatingAlpha == 1 ? `Rating ${displayRating} out of 5` : '') +
            speak(description) +
            speak(actorsAlpha ? `Cast: ${item.actors.join(', ')}` : '') +
            speak(directorsAlpha ? `Creators: ${item.directors.join(', ')}` : '')
        platform?.textToSpeach(speechText)

        return infoObj
    }

    _updateLogo() {
        const { appState } = AppData as AppSetup
        const appApiConfig = appState?.getAppApiConfig()
        this.tag('LogoContainer').patch({
            Logo: {
                texture: {
                    src: appApiConfig?.appBrandConfig?.header_logo,
                    resizeMode: {
                        h: 60,
                        w: 460,
                        type: 'contain',
                    },
                    type: Lightning.textures.ImageTexture
                },
            }
        })
    }

    set item(item: ItemNode) {
        if (!item) return;
        this.tag('Container').patch({
            InfoTextWrapper: this.getPatchObjects(item),
        })
    }

}